import { useState } from "react";

/** Host of a URL or bare domain (`https://www.acme.mx/x` → `acme.mx`), or null. */
export function domainOf(value?: string | null): string | null {
  const raw = (value ?? "").trim().toLowerCase();
  if (!raw) return null;
  const host = raw.replace(/^[a-z]+:\/\//, "").split(/[/?#]/)[0].replace(/^www\./, "");
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(host) ? host : null;
}

/**
 * Where to look for a company's picture, best first: the logo stored on the
 * company (HubSpot's `hs_logo_url`), then the icon of its website. HubSpot does
 * the same when a company has no logo of its own.
 */
export function companyLogoSources(company: { logoUrl?: string | null; domain?: string | null; website?: string | null }): string[] {
  const out: string[] = [];
  const logo = (company.logoUrl ?? "").trim();
  if (/^https?:\/\//i.test(logo)) out.push(logo);
  const domain = domainOf(company.domain) ?? domainOf(company.website);
  if (domain) out.push(`https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=64`);
  return out;
}

/** Up to two initials of a name («Acme Logistics» → «AL»). */
export function initialsOf(name?: string | null): string {
  const words = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "?";
  return (words[0][0] + (words[1]?.[0] ?? "")).toUpperCase();
}

interface Props {
  name?: string | null;
  logoUrl?: string | null;
  domain?: string | null;
  website?: string | null;
  /** Tailwind size classes; defaults to a 28 px square. */
  className?: string;
}

/**
 * The company's picture: its logo, else the icon of its website, else its
 * initials. A source that fails to load falls through to the next one.
 */
export function CompanyLogo({ name, logoUrl, domain, website, className = "h-7 w-7" }: Props) {
  const sources = companyLogoSources({ logoUrl, domain, website });
  const [failed, setFailed] = useState(0);
  const src = sources[failed];
  const box = `inline-flex shrink-0 items-center justify-center overflow-hidden rounded-md ${className}`;

  if (!src) {
    return (
      <span data-testid="company-logo-initials" aria-hidden className={`${box} bg-primary-500/15 text-[10px] font-bold text-primary-500`}>
        {initialsOf(name)}
      </span>
    );
  }
  return (
    <span className={`${box} bg-white`}>
      <img
        data-testid="company-logo"
        src={src}
        alt=""
        loading="lazy"
        referrerPolicy="no-referrer"
        onError={() => setFailed((n) => n + 1)}
        className="h-full w-full object-contain"
      />
    </span>
  );
}
